import axiosApiClient, { axiosApiPublicClient } from "..";
import { handleError } from "../../utils/errors";

const BASE_URL = "/model";

class ModelService {
  getCategories = async () => {
    try {
      const response = await axiosApiPublicClient.get(
        `/api${BASE_URL}/categories`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching categories in");
    }
  };

  fetchCollection = async ({ collectionId }) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/${collectionId}`);
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching collection details",
      );
    }
  };

  getCollections = async ({ page, search, filter }) => {
    try {
      let url = `${BASE_URL}/collections?page=${page}`;
      if (search && search.trim() !== "") {
        url += `&search=${encodeURIComponent(search)}`;
      }

      if (filter && filter.trim() !== "") {
        url += `&filter=${encodeURIComponent(filter)}`;
      }

      const response = await axiosApiClient.get(url);
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching all collections");
    }
  };

  getCollectionsByModelId = async ({ modelId, page = 1 }) => {
    try {
      const url = `${BASE_URL}/collections?modelId=${encodeURIComponent(
        modelId,
      )}&page=${page}`;

      const response = await axiosApiClient.get(url);
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching collections by model",
      );
    }
  };

  getUserFavorites = async ({ page, search, keyword }) => {
    try {
      let url = `${BASE_URL}/favorites/?page=${page}`;
      if (search && search.trim() !== "") {
        url += `&search=${encodeURIComponent(search)}`;
      }

      if (keyword && keyword.trim() !== "" && keyword.trim() !== "All") {
        url += `&keyword=${encodeURIComponent(keyword)}`;
      }

      const response = await axiosApiClient.get(url);
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching user favorites");
    }
  };

  getUserFollowing = async ({ page, search, keyword }) => {
    try {
      let url = `${BASE_URL}/following/?page=${page}`;
      if (search && search.trim() !== "") {
        url += `&search=${encodeURIComponent(search)}`;
      }

      if (keyword && keyword.trim() !== "" && keyword.trim() !== "All") {
        url += `&keyword=${encodeURIComponent(keyword)}`;
      }

      const response = await axiosApiClient.get(url);
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching user following");
    }
  };

  getExploreCollections = async ({ page, search, categoryId, keyword }) => {
    try {
      let url = `${BASE_URL}/explore/${categoryId ?? ""}?page=${page}`;
      if (search && search.trim() !== "") {
        url += `&search=${encodeURIComponent(search)}`;
      }

      if (keyword && keyword.trim() !== "" && keyword.trim() !== "All") {
        url += `&keyword=${encodeURIComponent(
          keyword === "Bizarre" ? "Disturbing" : keyword,
        )}`;
      }

      const response = await axiosApiClient.get(url);
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while fetching explore collections",
      );
    }
  };

  generateCollection = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/generate-collection`,
        requestData,
      );
      if (response.status !== 201) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Media generation failed due to an unexpected error. Please try again.",
      );
    }
  };

  generateBlendCollection = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/generate-blend-collection`,
        requestData,
      );
      if (response.status !== 201) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Media generation failed due to an unexpected error. Please try again.",
      );
    }
  };

  generateImageCollection = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/generate-body-on-text-collection`,
        requestData,
      );
      if (response.status !== 201) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Media generation failed due to an unexpected error. Please try again.",
      );
    }
  };

  regenerateCollection = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/regenerate-collection`,
        requestData,
      );
      if (response.status !== 201) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while regenerating collection");
    }
  };

  regenerateBlendCollection = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/regenerate-blend-collection`,
        requestData,
      );
      if (response.status !== 201) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while regenerating collection");
    }
  };

  regenerateImageCollection = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/regenerate-edit-photo-collection`,
        requestData,
      );
      if (response.status !== 201) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while regenerating collection");
    }
  };

  toggleToFavorite = async ({ collectionId }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/favorite/${collectionId}`,
      );
      if (response.status !== 201) {
        throw new Error();
      }

      return collectionId;
    } catch (err) {
      throw handleError(err, "Error occurred while toggle to favorite");
    }
  };

  togglePinCollection = async ({ collectionId }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/pin/${collectionId}`,
      );
      if (response.status !== 200 && response.status !== 201) {
        throw new Error();
      }

      return collectionId;
    } catch (err) {
      throw handleError(err, "Error occurred while toggling pin");
    }
  };

  deleteCollection = async (collections) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/collection/delete`,
        {
          collections,
        },
      );
      if (response.status !== 200) {
        throw new Error();
      }
      response.data.ids = collections;
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while deleting collection");
    }
  };

  hideCollection = async ({ collectionId, hide }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/collection/${collectionId}/hide`,
        { hide },
      );
      if (response.status !== 200) {
        throw new Error();
      }

      return {
        ...response.data,
        collectionId,
        hide,
      };
    } catch (err) {
      throw handleError(err, "Error occurred while hiding collection");
    }
  };

  hideCollectionVip = async ({ collectionId, hide }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/collection/${collectionId}/hide-vip`,
        { hide },
      );
      if (response.status !== 200) {
        throw new Error();
      }

      return {
        ...response.data,
        collectionId,
        hide,
      };
    } catch (err) {
      throw handleError(err, "Error occurred while hiding VIP collection");
    }
  };

  // Train model methods
  getTrainModels = async ({ page = 1 }) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/train?page=${page}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching train models");
    }
  };

  getTrainModel = async (trainModelId) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/train/${trainModelId}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching train model");
    }
  };

  createTrainModel = async ({ name, type }) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/train`, {
        name,
        type,
      });
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while creating train model");
    }
  };

  updateTrainModel = async ({ trainModelId, formData }) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/train/${trainModelId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while updating train model");
    }
  };

  deleteTrainModel = async (trainModelId) => {
    try {
      const response = await axiosApiClient.delete(
        `${BASE_URL}/train/${trainModelId}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      response.data.id = trainModelId;
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while deleting train model");
    }
  };

  getTopCreators = async () => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/collection/top-creators`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching top creators");
    }
  };
  rateModel = async ({ collectionId, rating }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/rate/${collectionId}`,
        { rating },
      );
      if (response.status !== 200) {
        throw new Error();
      }
      response.data.id = collectionId;
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while deleting collection");
    }
  };

  getDefaultGallery = async () => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/gallery?title=default`,
      );
      if (response.status !== 200) {
        throw new Error();
      }

      const data = response.data.data.images;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while getting gallery images");
    }
  };

  getUploadUrl = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/train/${requestData.trainModelId}/upload-url`,
        requestData,
      );
      if (response.status !== 200) {
        throw new Error();
      }

      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while creating upload url");
    }
  };

  selectCoverForTrainModel = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/train/${requestData.trainModelId}/select-cover`,
        requestData,
      );
      if (response.status !== 200) {
        throw new Error();
      }

      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while selecting cover");
    }
  };

  selectBlendCollectionForTrainModel = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/train/${requestData.trainModelId}/select-blend-collection`,
        requestData,
      );
      if (response.status !== 200) {
        throw new Error();
      }

      return response.data.data;
    } catch (err) {
      throw handleError(err, "Error occurred while selecting blend collection");
    }
  };

  selectTagsForTrainModel = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/train/${requestData.trainModelId}/select-tags`,
        requestData,
      );
      if (response.status !== 200) {
        throw new Error();
      }

      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while selecting tags");
    }
  };

  enableBlendForTrainModel = async ({ trainModelId }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/train/${trainModelId}/enable-blend`,
      );
      if (response.status !== 200 && response.status !== 201) {
        throw new Error();
      }

      return response.data.data;
    } catch (err) {
      throw handleError(err, "Error occurred while enabling blend mode");
    }
  };

  updateBlendTrainModel = async ({ trainModelId, name, groupPrompt }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/train/${trainModelId}/blend-model`,
        {
          ...(name != null && { name }),
          ...(groupPrompt != null && { groupPrompt }),
        },
      );
      if (response.status !== 200 && response.status !== 201) {
        throw new Error();
      }

      return response.data.data;
    } catch (err) {
      throw handleError(err, "Error occurred while updating blend model");
    }
  };

  submitTrainModelForReview = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/train/${requestData.trainModelId}/submit-review`,
        requestData,
      );
      if (response.status !== 200) {
        throw new Error();
      }

      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while submitting train model for review",
      );
    }
  };

  publishOnFapify = async ({ trainModelId, fapifyUsername }) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/train/${trainModelId}/publish`,
        { username: fapifyUsername },
      );
      if (response.status !== 200 && response.status !== 201) {
        throw new Error();
      }
      return response.data;
    } catch (err) {
      throw handleError(err, "Error occurred while publishing on Fapify.");
    }
  };

  getFolders = async () => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/folders`);
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching folders");
    }
  };

  createFolder = async ({ name }) => {
    try {
      const response = await axiosApiClient.post(`${BASE_URL}/folder`, {
        name,
      });
      if (response.status !== 201) {
        throw new Error();
      }
      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while creating folder");
    }
  };

  deleteFolder = async ({ folderId }) => {
    try {
      const response = await axiosApiClient.delete(
        `${BASE_URL}/folder/${folderId}`,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while deleting folder");
    }
  };

  editFolder = async ({ folderId, name }) => {
    try {
      const response = await axiosApiClient.put(
        `${BASE_URL}/folder/${folderId}`,
        { name },
      );
      if (response.status !== 201) {
        throw new Error();
      }
      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(err, "Error occurred while editing folder");
    }
  };

  putCollectionToFolder = async ({ collectionId, folderIds }) => {
    try {
      let requestData = { collectionId, folderIds };

      const response = await axiosApiClient.put(
        `${BASE_URL}/folder`,
        requestData,
      );
      if (response.status !== 200) {
        throw new Error();
      }
      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while adding collection to folder",
      );
    }
  };

  generateAnonymize = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/generate-anonymize`,
        requestData,
      );
      if (response.status !== 201) {
        throw new Error();
      }
      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Media generation failed due to an unexpected error. Please try again.",
      );
    }
  };

  getGroupCollections = async (type) => {
    try {
      const response = await axiosApiClient.get(
        `${BASE_URL}/group-collections?type=${type}`,
      );
      if (response.status !== 201) {
        throw new Error();
      }
      const data = response.data.data;
      return data;
    } catch (err) {
      throw handleError(
        err,
        "Fetch group collections failed. Please try again.",
      );
    }
  };

  selectCollectionsInGroupCollections = async (requestData) => {
    try {
      const response = await axiosApiClient.post(
        `${BASE_URL}/select-group-collections`,
        requestData,
      );
      if (response.status !== 201) {
        throw new Error();
      }
      const data = response.data.data;
      return { collections: data, group: requestData.collectionGroup };
    } catch (err) {
      throw handleError(err, "Select images failed. Please try again.");
    }
  };

  getUserGallery = async ({ page = 1 } = {}) => {
    try {
      const response = await axiosApiClient.get(`${BASE_URL}/user-gallery`, {
        params: { page },
      });
      if (response.status !== 200) {
        throw new Error();
      }
      return response.data.data;
    } catch (err) {
      throw handleError(err, "Error occurred while fetching user gallery.");
    }
  };

  addImageToUserGallery = async ({ file, fieldName }) => {
    try {
      const formData = new FormData();
      formData.append(fieldName, file, file.name);

      const response = await axiosApiClient.post(
        `${BASE_URL}/user-gallery/image`,
        formData,
      );
      if (response.status !== 200 && response.status !== 201) {
        throw new Error();
      }
      return response.data.data;
    } catch (err) {
      throw handleError(err, "Error occurred while adding image to gallery.");
    }
  };

  deleteUserGalleryImage = async ({ urls }) => {
    try {
      const response = await axiosApiClient.delete(
        `${BASE_URL}/user-gallery/image`,
        {
          data: { urls },
        },
      );
      if (response.status !== 200) {
        throw new Error();
      }
      return {
        data: response.data.data,
        urls,
      };
    } catch (err) {
      throw handleError(
        err,
        "Error occurred while deleting image from gallery.",
      );
    }
  };
}

const modelService = new ModelService();

export default modelService;
